// ===========================================
// SHIFT GUARD COMPONENT
// ===========================================
// Protects routes based on shift status
// Redirects to appropriate pages based on current shift state

import { ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useShiftStore } from '../stores/shiftStore';

interface ShiftGuardProps {
  children: ReactNode;
  requireActiveShift?: boolean;
}

export default function ShiftGuard({ children, requireActiveShift = true }: ShiftGuardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { status: shiftStatus } = useShiftStore();

  useEffect(() => {
    // Define allowed paths for each status
    const currentPath = location.pathname;

    // Pre-shift page is allowed when not in active shift
    if (currentPath === '/pre-shift') {
      if (shiftStatus === 'active') {
        navigate('/tables', { replace: true });
      }
      return;
    }

    // Post-shift page is allowed when ending shift
    if (currentPath === '/post-shift') {
      if (shiftStatus === 'no_shift' || shiftStatus === 'pre_shift') {
        navigate('/pre-shift', { replace: true });
      }
      return;
    }

    // For all other routes that require an active shift
    if (requireActiveShift) {
      if (shiftStatus === 'no_shift' || shiftStatus === 'pre_shift') {
        navigate('/pre-shift', { replace: true });
        return;
      }

      if (shiftStatus === 'post_shift') {
        navigate('/post-shift', { replace: true });
        return;
      }
    }
  }, [shiftStatus, location.pathname, navigate, requireActiveShift]);

  return <>{children}</>;
}
